import { useMutation, useQueryClient } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';

export interface UpdateClientStatusPayload {
  clientId: string;
  isActive?: boolean;
  isDeleted?: boolean;
}

export const updateClientStatus = async (payload: UpdateClientStatusPayload) => {
  const currentUser = sessionStore.getState().currentUser;
  const trainerId = currentUser?.uid || auth.currentUser?.uid;

  if (!trainerId) {
    throw new Error('Trainer ID is missing');
  }

  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_UPDATE_CLIENT_STATUS}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...payload, trainerId }),
  });

  if (!response.ok) {
    let errorMessage = `Failed to update client status: ${response.status}`;
    let errorCode = 'ERR_UNKNOWN';
    try {
      const errorData = await response.json();
      if (errorData.errorCode) {
        errorCode = errorData.errorCode;
      }
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch (e) {
      // Ignore json parse error
      console.error('Error updating client status', e);
    }
    const error: any = new Error(errorMessage);
    error.errorCode = errorCode;
    throw error;
  }

  return response.json();
};

export function useUpdateClientStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateClientStatus,
    onSuccess: () => {
      const currentUser = sessionStore.getState().currentUser;
      const trainerId = currentUser?.uid;
      if (trainerId) {
        // Invalidate trainer clients list so the UI refreshes
        queryClient.invalidateQueries({ queryKey: ['trainer-clients', trainerId] });
      }
    },
  });
}
